/**
 * BLRRD format dispatcher — magic-bytes detection + lazy-loaded decoders.
 *
 * Phase 3.A.0 (foundation, 2026-05-07):
 * Detects the format of any blob the user drops/picks, routes to the
 * right decoder. Native browsers handle PNG/JPEG/WebP/AVIF (and HEIC
 * on Safari macOS) — those return `{ kind: 'native' }` and the existing
 * `<img>` decode path runs unchanged. Unknown / not-yet-supported
 * formats throw a tagged `UnsupportedFormatError` with a friendly
 * message so the legacy editor can show "Coming Phase 3.B" toast
 * instead of silently dropping the file.
 *
 * Future phases (3.A.1 TIFF, 3.A.2 HEIC, 3.B RAW, 3.C EXR) plug in
 * by populating the `DECODERS` registry with `() => import('./decoders/<fmt>.js')`.
 *
 * Magic-bytes are checked first because most browsers leave
 * `blob.type === ''` for HEIC/TIFF/RAW. Falls back to file-extension
 * (when `blob.name` is set) and finally to the MIME hint.
 *
 * Decoder return contract (when implemented):
 *   { kind: 'native' }
 *     → existing <img>/createImageBitmap pipeline (no work here)
 *   { kind: 'rgba8',     rgba: Uint8ClampedArray, w, h,
 *                        colorSpace: 'srgb' }
 *     → ingestSRGB direct
 *   { kind: 'rgbaFloat', rgba: Float32Array, w, h,
 *                        colorSpace: 'linear-rec709'|'aces-ap0'|'aces-ap1'|'BT2020-PQ',
 *                        bitDepth:  16|32 }
 *     → matrix into ACEScg via ingestLinear* helper
 */

/**
 * Tagged error so the editor can show a friendly toast instead of
 * a raw stack trace. `name === 'UnsupportedFormatError'` is the gate.
 */
export class UnsupportedFormatError extends Error {
    constructor(format, message) {
        super(message);
        this.name = 'UnsupportedFormatError';
        this.format = format;     /* e.g. 'tiff', 'heic', 'raw-cr2' */
    }
}

/* Format identifier set. Stable strings — UI / future tests key off these. */
export const FORMATS = Object.freeze({
    PNG:      'png',
    JPEG:     'jpeg',
    WEBP:     'webp',
    AVIF:     'avif',
    GIF:      'gif',
    BMP:      'bmp',
    HEIC:     'heic',
    HEIF:     'heif',
    TIFF:     'tiff',
    DPX:      'dpx',
    EXR:      'exr',
    RAW_CR2:  'raw-cr2',
    RAW_CR3:  'raw-cr3',
    RAW_NEF:  'raw-nef',
    RAW_ARW:  'raw-arw',
    RAW_DNG:  'raw-dng',
    RAW_RAF:  'raw-raf',
    RAW_RW2:  'raw-rw2',
    RAW_ORF:  'raw-orf',
    RAW_X3F:  'raw-x3f',
    UNKNOWN:  'unknown',
});

/**
 * Native-decode set: browsers handle these via `<img>` /
 * `createImageBitmap`. AVIF support is widespread (>94%); HEIC works
 * on Safari macOS/iOS but not Chrome/Firefox/Edge yet, so HEIC is
 * NOT in the native set — it goes through libheif (Phase 3.A.2).
 */
const NATIVE_FORMATS = new Set([
    FORMATS.PNG, FORMATS.JPEG, FORMATS.WEBP, FORMATS.AVIF,
    FORMATS.GIF, FORMATS.BMP,
]);

/**
 * Lazy decoder registry. Keys are FORMATS values. Values are factory
 * functions returning a Promise that resolves to a module exporting a
 * `decode(blob)` function. Empty for now — Phase 3.A.1+ populates.
 */
const DECODERS = Object.freeze({
    /* Phase 3.A.1 — TIFF (UTIF.js, MIT, ~110KB lazy WASM-free).
     * Covers 8-bit + 16-bit RGB / RGBA / CMYK / YCbCr with
     * Uncompressed / LZW / Deflate / PackBits compression. */
    [FORMATS.TIFF]: () => import('./decoders/tiff.js'),
    /* Phase 3.A.2 — HEIC/HEIF (libheif-js, LGPL chain, ~1MB WASM
     * lazy-loaded). 8-bit sRGB output via libheif's display() API.
     * 10-bit BT.2020 PQ HDR HEICs decode but get tone-mapped to 8-bit
     * by libheif internally — full-precision HDR readback is a
     * Phase 3.A.2.1 follow-up. */
    [FORMATS.HEIC]: () => import('./decoders/heic.js'),
    [FORMATS.HEIF]: () => import('./decoders/heic.js'),
    /* Phase 3.B — Camera RAW family via libraw-wasm (LGPL-2.1 LibRaw,
     * ISC wrapper, ~2 MB gzipped lazy-load). All RAW format IDs route
     * to the same decoder; libraw handles per-camera dispatch internally
     * via magic-bytes + maker-note IFD inspection. The wrapper spawns
     * its own Web Worker, so our dispatcher thread stays responsive. */
    [FORMATS.RAW_CR2]: () => import('./decoders/raw.js'),
    [FORMATS.RAW_CR3]: () => import('./decoders/raw.js'),
    [FORMATS.RAW_NEF]: () => import('./decoders/raw.js'),
    [FORMATS.RAW_ARW]: () => import('./decoders/raw.js'),
    [FORMATS.RAW_DNG]: () => import('./decoders/raw.js'),
    [FORMATS.RAW_RAF]: () => import('./decoders/raw.js'),
    [FORMATS.RAW_RW2]: () => import('./decoders/raw.js'),
    [FORMATS.RAW_ORF]: () => import('./decoders/raw.js'),
    [FORMATS.RAW_X3F]: () => import('./decoders/raw.js'),
    /* Phase 3.C — OpenEXR via exrs (johannesvollmer Rust→WASM build,
     * BSD-3-Clause). RGBA fast-path with RGB fallback; un-premultiplies
     * alpha to match BLRRD's straight-alpha kernel contract. v1 assumes
     * linear-Rec709 primaries (chromaticities-tag dispatch is 3.C.1). */
    [FORMATS.EXR]: () => import('./decoders/exr.js'),
});

/**
 * Detect the format of a blob. Magic-bytes-first (most reliable),
 * extension fallback (for blobs with empty MIME), MIME hint last.
 *
 * @param {Blob} blob
 * @returns {Promise<string>}  one of FORMATS.*
 */
export async function detectFormat(blob) {
    if (!blob || typeof blob.arrayBuffer !== 'function') return FORMATS.UNKNOWN;

    /* Read just the first 16 bytes — enough for every format we care
     * about. createImageBitmap will re-read the whole blob anyway. */
    let head;
    try {
        const slice = blob.slice(0, 16);
        head = new Uint8Array(await slice.arrayBuffer());
    } catch {
        head = new Uint8Array(0);
    }

    /* Magic-bytes detection. Order: most-specific first so e.g. HEIC's
     * ftyp box (within JPEG-2000-shaped first bytes) is tested before
     * the generic ftyp catch. */
    if (matchMagic(head, [0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A]))
        return FORMATS.PNG;
    if (matchMagic(head, [0xFF, 0xD8, 0xFF]))
        return FORMATS.JPEG;
    if (head.length >= 12 && head[0] === 0x52 && head[1] === 0x49 && head[2] === 0x46 && head[3] === 0x46
            && head[8] === 0x57 && head[9] === 0x45 && head[10] === 0x42 && head[11] === 0x50)
        return FORMATS.WEBP;   /* RIFF....WEBP */
    if (head.length >= 12 && head[4] === 0x66 && head[5] === 0x74 && head[6] === 0x79 && head[7] === 0x70) {
        /* ISO BMFF box: bytes 8-11 are the brand. */
        const brand = String.fromCharCode(head[8], head[9], head[10], head[11]);
        if (brand === 'avif' || brand === 'avis') return FORMATS.AVIF;
        if (brand === 'heic' || brand === 'heix' || brand === 'mif1' || brand === 'msf1')
            return FORMATS.HEIC;
        if (brand === 'heif') return FORMATS.HEIF;
        if (brand === 'crx ' || brand === 'crx\0') return FORMATS.RAW_CR3;
    }
    if (matchMagic(head, [0x47, 0x49, 0x46, 0x38])) return FORMATS.GIF;
    if (matchMagic(head, [0x42, 0x4D])) return FORMATS.BMP;
    /* CR2 (Canon): TIFF II*\0 header + CR2 marker at offset 8-9.
     * MUST be checked BEFORE the generic TIFF detect (CR2 IS a TIFF
     * subtype). NEF and other RAWs that share TIFF magic are
     * indistinguishable by header alone — they get extension-fallback
     * routing below, OR they fall through to TIFF here and the
     * actual decoder layer disambiguates by IFD entries. */
    if ((head[0] === 0x49 && head[1] === 0x49 && head[2] === 0x2A && head[3] === 0x00
            && head[8] === 0x43 && head[9] === 0x52))
        return FORMATS.RAW_CR2;
    /* TIFF: II*\0 (little-endian) or MM\0* (big-endian). Use the
     * extension fallback later in this function to disambiguate
     * NEF / ARW / DNG / RW2 / ORF (all TIFF-shaped on disk). */
    if ((head[0] === 0x49 && head[1] === 0x49 && head[2] === 0x2A && head[3] === 0x00)
            || (head[0] === 0x4D && head[1] === 0x4D && head[2] === 0x00 && head[3] === 0x2A)) {
        /* Check extension — many RAW formats are TIFF-shaped.
         * If a RAW extension is set, prefer the RAW format ID. */
        const nm = (blob.name || '').toLowerCase();
        if (/\.nef$/.test(nm)) return FORMATS.RAW_NEF;
        if (/\.arw$/.test(nm)) return FORMATS.RAW_ARW;
        if (/\.dng$/.test(nm)) return FORMATS.RAW_DNG;
        if (/\.rw2$/.test(nm)) return FORMATS.RAW_RW2;
        if (/\.orf$/.test(nm)) return FORMATS.RAW_ORF;
        return FORMATS.TIFF;
    }
    /* OpenEXR: 0x76 0x2F 0x31 0x01 */
    if (matchMagic(head, [0x76, 0x2F, 0x31, 0x01])) return FORMATS.EXR;
    /* DPX: SDPX (LE) or XPDS (BE) */
    if (matchMagic(head, [0x53, 0x44, 0x50, 0x58])) return FORMATS.DPX;
    if (matchMagic(head, [0x58, 0x50, 0x44, 0x53])) return FORMATS.DPX;
    /* RAF (Fuji): "FUJIFILMCCD-RAW" */
    if (head.length >= 8 && head[0] === 0x46 && head[1] === 0x55 && head[2] === 0x4A
            && head[3] === 0x49 && head[4] === 0x46 && head[5] === 0x49 && head[6] === 0x4C && head[7] === 0x4D)
        return FORMATS.RAW_RAF;
    /* X3F (Sigma Foveon): "FOVb" */
    if (matchMagic(head, [0x46, 0x4F, 0x56, 0x62])) return FORMATS.RAW_X3F;

    /* Extension fallback — most browsers leave .heic/.tiff/.cr2/etc.
     * with empty MIME, so the user-friendly drag-drop path needs this. */
    const name = (blob.name || '').toLowerCase();
    const m = name.match(/\.([a-z0-9]+)$/);
    if (m) {
        switch (m[1]) {
            case 'heic': case 'hif':            return FORMATS.HEIC;
            case 'heif':                        return FORMATS.HEIF;
            case 'tif': case 'tiff':            return FORMATS.TIFF;
            case 'dpx':                         return FORMATS.DPX;
            case 'exr':                         return FORMATS.EXR;
            case 'cr2':                         return FORMATS.RAW_CR2;
            case 'cr3':                         return FORMATS.RAW_CR3;
            case 'nef':                         return FORMATS.RAW_NEF;
            case 'arw':                         return FORMATS.RAW_ARW;
            case 'dng':                         return FORMATS.RAW_DNG;
            case 'raf':                         return FORMATS.RAW_RAF;
            case 'rw2':                         return FORMATS.RAW_RW2;
            case 'orf':                         return FORMATS.RAW_ORF;
            case 'x3f':                         return FORMATS.RAW_X3F;
            case 'avif':                        return FORMATS.AVIF;
            case 'webp':                        return FORMATS.WEBP;
            case 'png':                         return FORMATS.PNG;
            case 'jpg': case 'jpeg':            return FORMATS.JPEG;
            case 'gif':                         return FORMATS.GIF;
            case 'bmp':                         return FORMATS.BMP;
        }
    }

    /* MIME hint last — least reliable but covers misnamed files
     * served by trustworthy sources. */
    const mime = (blob.type || '').toLowerCase();
    if (mime.startsWith('image/')) {
        if (mime.includes('png'))  return FORMATS.PNG;
        if (mime.includes('jpeg') || mime.includes('jpg')) return FORMATS.JPEG;
        if (mime.includes('webp')) return FORMATS.WEBP;
        if (mime.includes('avif')) return FORMATS.AVIF;
        if (mime.includes('heic') || mime.includes('heif')) return FORMATS.HEIC;
        if (mime.includes('tiff')) return FORMATS.TIFF;
    }

    return FORMATS.UNKNOWN;
}

/**
 * Decode a blob. Native formats return `{ kind: 'native' }` so the
 * caller's existing `<img>` pipeline runs unchanged. Format-specific
 * decoders (Phase 3.A.1+) return `{ kind: 'rgba8' | 'rgbaFloat', ... }`.
 *
 * Throws `UnsupportedFormatError` for formats that ARE recognized but
 * don't have a decoder yet — the caller should catch this and show
 * a friendly toast pointing at the planning phase.
 *
 * @param {Blob} blob
 * @returns {Promise<{kind:'native'} | {kind:'rgba8'|'rgbaFloat', rgba, w, h, colorSpace, bitDepth?}>}
 */
export async function decode(blob) {
    const format = await detectFormat(blob);

    if (NATIVE_FORMATS.has(format)) {
        return { kind: 'native', format };
    }

    if (format in DECODERS) {
        const mod = await DECODERS[format]();
        if (typeof mod.decode !== 'function') {
            throw new UnsupportedFormatError(format,
                `Decoder module for ${format} is missing a decode() export`);
        }
        return mod.decode(blob);
    }

    if (format === FORMATS.UNKNOWN) {
        throw new UnsupportedFormatError(format,
            'Format not recognised. BLRRD currently supports PNG, JPEG, WebP, ' +
            'AVIF, GIF, BMP. HEIC + TIFF + EXR + camera RAWs are coming in ' +
            'Phase 3.A and 3.B.');
    }

    /* Recognized but no decoder yet — friendly Phase pointer. */
    const phaseHint = phaseFor(format);
    throw new UnsupportedFormatError(format,
        `${formatLabel(format)} input is coming ${phaseHint}. For now, please ` +
        `convert to PNG / JPEG / WebP / AVIF and try again.`);
}

/* ---- helpers ----------------------------------------------------- */

function matchMagic(buf, sig) {
    if (buf.length < sig.length) return false;
    for (let i = 0; i < sig.length; i++) if (buf[i] !== sig[i]) return false;
    return true;
}

/** Human-readable labels for friendly error messages. */
function formatLabel(format) {
    if (format === FORMATS.HEIC || format === FORMATS.HEIF) return 'Apple HEIC/HEIF';
    if (format === FORMATS.TIFF) return 'TIFF';
    if (format === FORMATS.DPX)  return 'Cinema DPX';
    if (format === FORMATS.EXR)  return 'OpenEXR';
    if (format && format.startsWith('raw-')) return 'Camera RAW (' + format.slice(4).toUpperCase() + ')';
    return format;
}

/** Which Phase plans to ship support for this format. */
function phaseFor(format) {
    if (format === FORMATS.HEIC || format === FORMATS.HEIF) return 'in Phase 3.A.2';
    if (format === FORMATS.TIFF) return 'in Phase 3.A.1';
    if (format === FORMATS.EXR)  return 'in Phase 3.C';
    if (format === FORMATS.DPX)  return 'later (cinema-only, low priority)';
    if (format && format.startsWith('raw-')) return 'in Phase 3.B (libraw integration)';
    return 'in a later phase';
}
